"use client";

import { useEffect, useCallback, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import gsap from "gsap";
import "./lightbox.scss";

interface LightboxProps {
	images: Array<{
		id: number;
		image: string;
		alt: string;
		width: number;
		height: number;
		lgImage: string;
		lgImgWidth: number;
		lgImgHeight: number;
		
	}>;
	currentIndex: number;
	onClose: () => void;
	onNavigate: (direction: "prev" | "next") => void;
}

const Lightbox = ({ images, currentIndex, onClose, onNavigate }: LightboxProps) => {
	const [mounted, setMounted] = useState(false);
	const currentImage = images[currentIndex];

	useEffect(() => {
		setMounted(true);
		return () => setMounted(false);
	}, []);

	// Lock body scroll when lightbox is open
	useEffect(() => {
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = "";
		};
	}, []);

	// Handle keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
			if (e.key === "ArrowLeft") onNavigate("prev");
			if (e.key === "ArrowRight") onNavigate("next");
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [onClose, onNavigate]);

	// Entrance animation
	useEffect(() => {
		if (!mounted) return;
		const tl = gsap.timeline();
		tl.fromTo(
			".lightbox",
			{
				opacity: 0,
			},
			{
				opacity: 1,
				duration: 0.3,
				ease: "power2.out",
			}
		);
		tl.fromTo(
			".lightbox__content",
			{
				scale: 0.9,
				opacity: 0,
			},
			{
				scale: 1,
				opacity: 1,
				duration: 0.4,
				ease: "back.out(1.2)",
			},
			"-=0.2"
		);
	}, [mounted]);

	const handleClose = useCallback(() => {
		const tl = gsap.timeline({
			onComplete: onClose,
		});
		tl.to(".lightbox__content", {
			scale: 0.9,
			opacity: 0,
			duration: 0.3,
			ease: "power2.in",
		});
		tl.to(
			".lightbox",
			{
				opacity: 0,
				duration: 0.2,
				ease: "power2.in",
			},
			"-=0.1"
		);
	}, [onClose]);

	if (!mounted) return null;

	return createPortal(
		<div className="lightbox" onClick={handleClose}>
			<button className="lightbox__close" onClick={handleClose} aria-label="Close lightbox">
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</button>

			<button
				className="lightbox__nav lightbox__nav--prev"
				onClick={(e) => {
					e.stopPropagation();
					onNavigate("prev");
				}}
				aria-label="Previous image"
			>
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</button>

			<div className="lightbox__content" onClick={(e) => e.stopPropagation()}>
				<Image src={currentImage.lgImage} alt={currentImage.alt} width={currentImage.lgImgWidth} height={currentImage.lgImgWidth} className="lightbox__image" priority />
				<div className="lightbox__counter">
					{currentIndex + 1} / {images.length}
				</div>
			</div>

			<button
				className="lightbox__nav lightbox__nav--next"
				onClick={(e) => {
					e.stopPropagation();
					onNavigate("next");
				}}
				aria-label="Next image"
			>
				<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</button>
		</div>,
		document.body
	);
};

export default Lightbox;
